// ========================================
// SMS length helpers shared by SmsField and the messages composer.
// Rules are described at the top of src/components/SmsField.vue.
// ========================================

export const SMS_MAX_PARTS = 6

// Delivery status tone from BE -> color and icon (conversation thread and message history)
export const SMS_STATUS_TONES = {
  pending: { color: 'warning', icon: 'ri-time-line' },
  info: { color: '', icon: 'ri-check-line' },
  success: { color: 'green', icon: 'ri-check-double-line' },
  error: { color: 'error', icon: 'ri-error-warning-line' },
}

// true when the text fits the GSM alphabet (no unicode encoding needed)
export const isValidNoUnicode = (content) => {
  return !/[^\@\£\$\¥\è\é\ù\ì\ò\Ç\Ø\ø\Å\å\Δ\_\Φ\Γ\Λ\Ω\Π\Ψ\Σ\Θ\Ξ\^\{\}\\[\~\]\|\Æ\æ\ß\É\!\\\"\#\¤\%\&\\\'\(\)\*\+\,\-\.\/0123456789:;<=>?\¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà€ \r\n]/.test(content)
}

export const getTextLength = (content, unicode) => {
  let _content = content.replace(/(\n\r|\r\n)/, '\n')

  // Handle special placeholders
  _content = _content.replace(/(\[%.*?%\])/, '12345')

  if (unicode === 1) return _content.length

  // Count extended GSM characters as 2
  return _content.replace(/([\^\[\]{}~|\\€])/g, ' $1').length
}

export const getPartsCount = (length, unicode) => {
  if (unicode === 1) {
    return length <= 70 ? 1 : Math.ceil(length / 67)
  }

  return length <= 160 ? 1 : Math.ceil(length / 153)
}

const DIACRITICS = {
  'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ż': 'z', 'ź': 'z',
  'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N', 'Ó': 'O', 'Ś': 'S', 'Ż': 'Z', 'Ź': 'Z',
}

// Text as sent with "replace special characters": Polish diacritics -> latin, other non-GSM characters dropped
export const normalizeText = (content) => {
  return content
    .replace(/[ąćęłńóśżźĄĆĘŁŃÓŚŻŹ]/g, char => DIACRITICS[char])
    .replace(/[^\@\£\$\¥\è\é\ù\ì\ò\Ç\Ø\ø\Å\å\Δ\_\Φ\Γ\Λ\Ω\Π\Ψ\Σ\Θ\Ξ\^\{\}\\[\~\]\|\Æ\æ\ß\É\!\\\"\#\¤\%\&\\\'\(\)\*\+\,\-\.\/0123456789:;<=>?\¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà€ \r\n]/g, '')
}

// Characters that fit in the given number of parts - each part of a multipart SMS loses some characters to the header
const getCapacity = (unicode, parts) => {
  const { single, many } = unicode === 1 ? { single: 70, many: 67 } : { single: 160, many: 153 }

  return parts === 1 ? single : parts * many
}

// Characters left until the current SMS part is full
export const countLeftCharacters = (unicode, length, parts) => {
  return getCapacity(unicode, parts) - Math.min(length, getCapacity(unicode, SMS_MAX_PARTS))
}

// Translation key of the word "character" for n (Polish: 1 znak, 2-4 znaki, 5+ znaków, 12-14 znaków)
export const charNameKey = (n) => {
  if (n === 1) return 'charNameSingular'
  const tens = n % 100

  return n % 10 >= 2 && n % 10 <= 4 && (tens < 12 || tens > 14) ? 'charNamePlural2' : 'charNamePlural'
}

/**
 * Length, SMS parts and capacity of a message. An empty message counts as 1 SMS, like in SmsField.
 * @param {string} text
 * @param {boolean} normalize - count the text as sent with "replace special characters"
 * @returns {{ unicode: number, length: number, parts: number, capacity: number }} unicode: 1 = unicode encoding, 0 = GSM;
 *   capacity: characters that fit in the parts (at most SMS_MAX_PARTS), so length > capacity when over the limit
 */
export const getSmsInfo = (text, normalize = false) => {
  const content = normalize ? normalizeText(text) : text
  const unicode = isValidNoUnicode(content) ? 0 : 1
  const length = getTextLength(content, unicode)
  const parts = getPartsCount(length, unicode)

  return { unicode, length, parts, capacity: getCapacity(unicode, Math.min(parts, SMS_MAX_PARTS)) }
}
